import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Input,
  Text,
  Tooltip
} from "@mantine/core";
import StatsField from "./reusable/StatsField";
import { IconCheck, IconKey, IconX } from "@tabler/icons";
import { useGetFromPathQuery } from "../APIs/MirrorNode.api";
import { proto } from "@hashgraph/proto";
import {
  Client,
  ContractId,
  PublicKey,
  PrivateKey,
  TokenId,
  TokenUpdateTransaction
} from "headstarter-crypto/hashgraph-sdk";
import { useMemo, useState } from "react";
import Utils from "../utils/utilities";
import CopyableText from "./reusable/CopyableText";
import {
  useAccountInfo,
  useWallet
} from "@buidlerlabs/hashgraph-react-wallets";
import { showNotification, updateNotification } from "@mantine/notifications";
import { DEFAULT_NOTIFICATION_DELAY, IS_MAINNET } from "../utils/constants";
import HederaAddress from "./reusable/HederaAddress";

const TokenSupplyKeyToggle = ({ contractId, tokenId }) => {
  const { signer } = useWallet();
  const { data: connectedAccountInfo } = useAccountInfo();
  const { data: tokenInfo, refetch: refetchTokenInfo } = useGetFromPathQuery(
    `/api/v1/tokens/${tokenId}`
  );

  const [tokenPrivateKey, setTokenPrivateKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const onChangeTokenPKey = (e) => {
    const sKey = e.target.value;

    try {
      const pKey = PrivateKey.fromStringDer(sKey.trim());
      setTokenPrivateKey(pKey);
    } catch (_) {
      setTokenPrivateKey(null);
    }
  };

  const handleSwitchSupplyKey = async (toContract) => {
    setIsLoading(true);

    showNotification({
      id: "switch_token_supply_key",
      loading: true,
      title: "Update Supply Key",
      message: `Updating supply key for ${tokenId}`,
      autoClose: false,
      disallowClose: true
    });

    try {
      if (!tokenPrivateKey)
        throw new Error("Invalid token private key provided.");

      const connectedAccountPublicKey = PublicKey.fromString(
        connectedAccountInfo.key.key
      );

      const updateTx = new TokenUpdateTransaction()
        .setTokenId(TokenId.fromString(tokenId))
        .setSupplyKey(
          toContract
            ? ContractId.fromString(contractId)
            : connectedAccountPublicKey
        );

      const client = IS_MAINNET ? Client.forMainnet() : Client.forTestnet();
      const freezeTx = await updateTx.freezeWithSigner(signer);
      const signTx = await freezeTx.sign(tokenPrivateKey);
      const response = await signTx.executeWithSigner(signer);

      await response.getReceipt(client);

      setIsLoading(false);
      refetchTokenInfo();
      updateNotification({
        id: "switch_token_supply_key",
        color: "teal",
        title: "Update Supply Key",
        message: "Supply Key successfully updated",
        icon: <IconCheck size={16} />,
        autoClose: DEFAULT_NOTIFICATION_DELAY
      });
    } catch (e) {
      console.error(e);

      const errorMessage = JSON.stringify(e.message).includes("USER_REJECT")
        ? "User rejected action"
        : e.message;

      setIsLoading(false);
      updateNotification({
        id: "switch_token_supply_key",
        color: "red",
        title: "Update Supply Key Failed",
        message: errorMessage,
        icon: <IconX size={16} />,
        autoClose: 4000
      });
    }
  };

  const supplyKey = useMemo(() => {
    const supply_key = tokenInfo ? tokenInfo.supply_key : null;

    if (!supply_key) return null;

    switch (supply_key._type) {
      case "ProtobufEncoded":
        const kBuffer = Buffer.from(supply_key.key, "hex");
        const kDecoded = proto.Key.decode(kBuffer);

        if (!kDecoded) return null;
        if (kDecoded.contractID) {
          const contractNum = kDecoded.contractID.contractNum.toNumber();
          const shardNum = kDecoded.contractID.shardNum.toNumber?.() ?? 0;
          const realmNum = kDecoded.contractID.realmNum.toNumber?.() ?? 0;

          return {
            type: "Contract",
            key: new ContractId(shardNum, realmNum, contractNum).toString()
          };
        }

        return null;
      case "ED25519":
        return {
          type: supply_key._type,
          key: PublicKey.fromStringED25519(supply_key.key).toStringRaw()
        };
      case "ECDSA_SECP256K1":
        return {
          type: supply_key._type,
          key: PublicKey.fromStringECDSA(supply_key.key).toStringRaw()
        };
      default:
        return null;
    }
  }, [tokenInfo]);

  const stringSupplyKeyLabel =
    supplyKey?.type === "ED25519" || supplyKey?.type === "ECDSA_SECP256K1"
      ? Utils.shortenEthereumAddress(supplyKey.key, 10)
      : supplyKey?.key
        ? supplyKey.key
        : "-";

  const keyIsAccount = supplyKey?.type !== "Contract";
  const keyIsStorefront = supplyKey?.key === contractId;

  return (
    <Group position="apart" grow>
      <StatsField
        label={
          <Group spacing="xs">
            <Text size="xs">Supply Key</Text>
            <Group spacing={0}>
              <Badge size="xs" color={keyIsAccount ? "orange" : "green"}>
                {supplyKey?.type}
              </Badge>
              {keyIsStorefront && (
                <Tooltip label="Supply key is the actual storefront">
                  <ActionIcon variant="transparent" size="sm" color="green">
                    <IconCheck />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>
        }
        value={
          <CopyableText copy={supplyKey?.key}>
            {supplyKey?.type === "Contract" ? (
              <HederaAddress contractId={supplyKey?.key}>
                <Text size="xs">{stringSupplyKeyLabel}</Text>
              </HederaAddress>
            ) : (
              <Text size="xs">{stringSupplyKeyLabel}</Text>
            )}
          </CopyableText>
        }
        icon={IconKey}
      />
      <Group position="right" align="flex-end" spacing={5} noWrap>
        <Input
          size="xs"
          placeholder="Token Private Key"
          onChange={onChangeTokenPKey}
        />
        <Button
          size="xs"
          onClick={() => handleSwitchSupplyKey(keyIsAccount)}
          loading={isLoading}
          disabled={!tokenPrivateKey}
        >
          {keyIsAccount ? "Switch to contract" : "Swith to account"}
        </Button>
      </Group>
    </Group>
  );
};

export default TokenSupplyKeyToggle;
